import { canonicalJson } from './pack-engine';

/**
 * Board packs are deliberately aggregate-only.  Keeping this boundary in the
 * type system means a loader cannot accidentally pass names, narratives or
 * record identifiers into an immutable board artifact.
 */
export type BoardMetric = Readonly<{
  current: number | null;
  previous: number | null;
  denominator?: number;
  latestSourceDate: string | null;
}>;

export type BoardPackInputs = Readonly<{
  facilityId?: string;
  period: Readonly<{ start: string; end: string }>;
  asOf: string;
  stalenessDays: number;
  incidents: BoardMetric;
  sirs: BoardMetric;
  complaints: BoardMetric;
  restrictivePractices: BoardMetric;
  qualityImprovement: BoardMetric;
  workforceCompliance: BoardMetric;
  deadlines: BoardMetric;
  sha256Hex: (canonical: string) => string;
}>;

export type BoardPackPolicy = Readonly<{
  cadence: 'MONTHLY' | 'QUARTERLY';
  dayOfMonth: number;
  enabled: boolean;
}>;

export type BoardPackDocument = Readonly<{
  kind: 'BOARD_PACK';
  period: Readonly<{ start: string; end: string }>;
  facilityId?: string;
  stale: boolean;
  sections: readonly Readonly<{
    key:
      | 'INCIDENTS_SIRS'
      | 'COMPLAINTS'
      | 'RESTRICTIVE_PRACTICES'
      | 'QUALITY_IMPROVEMENT'
      | 'WORKFORCE_COMPLIANCE'
      | 'DEADLINES';
    metrics: readonly Readonly<{
      key: string;
      count: number | null;
      previousCount: number | null;
      rate: number | null;
      trend: 'UP' | 'DOWN' | 'FLAT' | 'UNAVAILABLE';
      status: 'AVAILABLE' | 'UNAVAILABLE';
      latestSourceDate: string | null;
      stale: boolean;
    }>[];
  }>[];
  contentDigest: string;
}>;

function assertFiniteMetric(metric: BoardMetric): void {
  if (
    (metric.current !== null && !Number.isFinite(metric.current)) ||
    (metric.previous !== null && !Number.isFinite(metric.previous)) ||
    (metric.current !== null && metric.current < 0) ||
    (metric.previous !== null && metric.previous < 0) ||
    (metric.denominator !== undefined &&
      (!Number.isFinite(metric.denominator) || metric.denominator < 0)) ||
    (metric.latestSourceDate !== null && !/^\d{4}-\d{2}-\d{2}$/.test(metric.latestSourceDate))
  ) {
    throw new Error('board_pack_metric_invalid');
  }
}

function dateFromYmd(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  const result = new Date(Date.UTC(year ?? 0, (month ?? 0) - 1, day ?? 0));
  if (
    result.getUTCFullYear() !== year ||
    result.getUTCMonth() !== (month ?? 0) - 1 ||
    result.getUTCDate() !== day
  ) {
    throw new Error('board_pack_date_invalid');
  }
  return result;
}

function metric(key: string, source: BoardMetric, asOf: Date, stalenessDays: number) {
  assertFiniteMetric(source);
  const sourceDate = source.latestSourceDate ? dateFromYmd(source.latestSourceDate) : null;
  const stale =
    sourceDate !== null && asOf.getTime() - sourceDate.getTime() > stalenessDays * 86_400_000;
  return {
    key,
    count: source.current,
    previousCount: source.previous,
    status: source.current === null ? ('UNAVAILABLE' as const) : ('AVAILABLE' as const),
    latestSourceDate: source.latestSourceDate,
    rate:
      source.current === null || source.denominator === undefined || source.denominator === 0
        ? null
        : // eslint-disable-next-line statura/no-floating-point-money -- a trend ratio of two counts, not a monetary amount
          Number(((source.current / source.denominator) * 100).toFixed(2)),
    trend:
      source.current === null || source.previous === null
        ? ('UNAVAILABLE' as const)
        : source.current > source.previous
          ? ('UP' as const)
          : source.current < source.previous
            ? ('DOWN' as const)
            : ('FLAT' as const),
    stale,
  };
}

/** Builds the deterministic, de-identified board view from count/rate inputs only. */
export function buildBoardPack(input: BoardPackInputs): BoardPackDocument {
  if (
    !input.period.start ||
    !input.period.end ||
    !Number.isInteger(input.stalenessDays) ||
    input.stalenessDays < 0
  ) {
    throw new Error('board_pack_scope_invalid');
  }
  dateFromYmd(input.period.start);
  dateFromYmd(input.period.end);
  if (input.period.start > input.period.end) throw new Error('board_pack_scope_invalid');
  const asOf = dateFromYmd(input.asOf.slice(0, 10));
  const incidents = metric('incidents', input.incidents, asOf, input.stalenessDays);
  const sirs = metric('sirs', input.sirs, asOf, input.stalenessDays);
  const complaints = metric('complaints', input.complaints, asOf, input.stalenessDays);
  const restrictivePractices = metric(
    'restrictive_practices',
    input.restrictivePractices,
    asOf,
    input.stalenessDays,
  );
  const qualityImprovement = metric(
    'quality_improvement',
    input.qualityImprovement,
    asOf,
    input.stalenessDays,
  );
  const workforceCompliance = metric(
    'workforce_compliance',
    input.workforceCompliance,
    asOf,
    input.stalenessDays,
  );
  const deadlines = metric('deadlines', input.deadlines, asOf, input.stalenessDays);
  const sections = [
    { key: 'INCIDENTS_SIRS' as const, metrics: [incidents, sirs] },
    { key: 'COMPLAINTS' as const, metrics: [complaints] },
    { key: 'RESTRICTIVE_PRACTICES' as const, metrics: [restrictivePractices] },
    { key: 'QUALITY_IMPROVEMENT' as const, metrics: [qualityImprovement] },
    { key: 'WORKFORCE_COMPLIANCE' as const, metrics: [workforceCompliance] },
    { key: 'DEADLINES' as const, metrics: [deadlines] },
  ];
  const draft = {
    kind: 'BOARD_PACK' as const,
    period: input.period,
    ...(input.facilityId ? { facilityId: input.facilityId } : {}),
    stale: sections.some((section) => section.metrics.some((item) => item.stale)),
    sections,
  };
  return { ...draft, contentDigest: input.sha256Hex(canonicalJson(draft)) };
}

function localYmd(now: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

function periodKey(year: number, month: number, cadence: BoardPackPolicy['cadence']): string {
  if (cadence === 'MONTHLY') return `${year}-${String(month).padStart(2, '0')}`;
  return `${year}-Q${Math.floor((month - 1) / 3) + 1}`;
}

/**
 * Computes due state in the tenant timezone.  `lastRunPeriod` makes duplicate
 * cron ticks harmless before the database's unique run receipt is consulted.
 * The key identifies the preceding completed period; runOn stays in the run month.
 */
export function nextBoardPackRun(
  policy: BoardPackPolicy,
  lastRunPeriod: string | null | undefined,
  now: Date,
  timezone: string,
): Readonly<{ due: boolean; periodKey: string; runOn: string }> {
  if (!Number.isInteger(policy.dayOfMonth) || policy.dayOfMonth < 1 || policy.dayOfMonth > 31) {
    throw new Error('board_pack_schedule_day_invalid');
  }
  const [yearText, monthText, dayText] = localYmd(now, timezone).split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const quarterStart = Math.floor((month - 1) / 3) * 3 + 1;
  const completedStart = new Date(
    Date.UTC(year, policy.cadence === 'QUARTERLY' ? quarterStart - 4 : month - 2, 1),
  );
  const key = periodKey(
    completedStart.getUTCFullYear(),
    completedStart.getUTCMonth() + 1,
    policy.cadence,
  );
  if (policy.cadence === 'QUARTERLY' && month !== quarterStart) {
    return {
      due: false,
      periodKey: key,
      runOn: `${year}-${String(quarterStart).padStart(2, '0')}-01`,
    };
  }
  const targetMonth = policy.cadence === 'QUARTERLY' ? quarterStart : month;
  const lastDay = new Date(Date.UTC(year, targetMonth, 0)).getUTCDate();
  const runOn = `${year}-${String(targetMonth).padStart(2, '0')}-${String(Math.min(policy.dayOfMonth, lastDay)).padStart(2, '0')}`;
  return {
    due: policy.enabled && day >= Math.min(policy.dayOfMonth, lastDay) && lastRunPeriod !== key,
    periodKey: key,
    runOn,
  };
}
